import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { Menu, Select, FormControl } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { Link } from 'react-router-dom';
import thermopied from './assets/product/thermopied.png';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';

function NavBar() {
    const [anchorElNav, setAnchorElNav] = React.useState(null);
    const { language, setLanguage } = useLanguage();
    const t = translations[language].nav;

    const views = [
        { key: 'home', label: t.home, path: '/' },
        { key: 'product', label: t.product, path: '/product' },
        { key: 'installation', label: t.installation, path: '/installation' },
        { key: 'manual', label: t.manual, path: '/usermanual' },
        { key: 'about', label: t.about, path: '/aboutus' },
        { key: 'contact', label: t.contact, path: '/contact' }
    ];

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleLanguageChange = (event) => {
        setLanguage(event.target.value);
    };

    return (
        <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)' }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters sx={{ minHeight: { xs: '70px', md: '80px' }, height: { xs: '70px', md: '80px' } }}>
                    {/* Desktop Layout */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }} >
                        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <img
                                src={thermopied}
                                alt="ThermoPied"
                                style={{
                                    marginRight: '24px',
                                    height: '64px',
                                    width: 'auto',
                                    cursor: 'pointer',
                                    objectFit: 'contain',
                                    transform: 'translateY(-6px)'
                                }}
                            />
                        </Link>
                    </Box>

                    {/* Mobile Layout */}
                    <Stack
                        direction="row"
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            display: { xs: 'flex', md: 'none' }
                        }}
                    >
                        {/* Hamburger Menu */}
                        <Box>
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleOpenNavMenu}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorElNav}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                open={Boolean(anchorElNav)}
                                onClose={handleCloseNavMenu}
                                sx={{ display: { xs: 'block', md: 'none' } }}
                            >
                                {views.map((view) => (
                                    <MenuItem key={view.key} onClick={handleCloseNavMenu}>
                                        <Typography textAlign="center">
                                            <Link to={view.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                {view.label}
                                            </Link>
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Centered Logo */}
                        <Box>
                            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                    <img
                                        src={thermopied}
                                        alt="ThermoPied"
                                        style={{
                                            height: '56px',
                                            width: 'auto',
                                            cursor: 'pointer',
                                            objectFit: 'contain',
                                            transform: 'translateY(-4px)'
                                        }}
                                    />
                                </Link>
                        </Box>

                        {/* Language Switcher */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon sx={{ mr: { xs: 0.5, md: 1 }, color: 'white', fontSize: { xs: '1.2rem', md: '1.5rem' }, display: { xs: 'none', sm: 'block' } }} />
                            <FormControl size="small">
                                <Select
                                    value={language}
                                    onChange={handleLanguageChange}
                                    sx={{
                                        color: 'white',
                                        minWidth: { xs: '60px', md: '100px' },
                                        fontSize: { xs: '0.8rem', md: '1rem' },
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                        '.MuiSvgIcon-root': {
                                            color: 'white',
                                        },
                                    }}
                                >
                                    <MenuItem value="en">EN</MenuItem>
                                    <MenuItem value="fr">FR</MenuItem>
                                    <MenuItem value="zh">中文</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>

                    {/* Desktop Menu */}
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                        {views.map((view) => (
                            <MenuItem key={view.key} onClick={handleCloseNavMenu}>
                                <Typography textAlign="center">
                                    <Link to={view.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        {view.label}
                                    </Link>
                                </Typography>
                            </MenuItem>
                        ))}
                    </Box>

                    {/* Desktop Language Switcher */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 0 }}>
                        <LanguageIcon sx={{ mr: 1, color: 'white', fontSize: '1.5rem' }} />
                        <FormControl size="small">
                            <Select
                                value={language}
                                onChange={handleLanguageChange}
                                sx={{
                                    color: 'white',
                                    minWidth: '100px',
                                    fontSize: '1rem',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white',
                                    },
                                    '.MuiSvgIcon-root': {
                                        color: 'white',
                                    },
                                }}
                            >
                                <MenuItem value="en">EN</MenuItem>
                                <MenuItem value="fr">FR</MenuItem>
                                <MenuItem value="zh">中文</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default NavBar;